import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCheckCircleDuotone, PiPlus, PiTrash, PiCheck } from "react-icons/pi";

export default function FeaturesList({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const features = content.features || [
        { title: "Natural Language", description: "Human-like text generation and understanding." },
        { title: "Computer Vision", description: "Advanced image recognition and object detection." },
        { title: "Predictive Analytics", description: "Forecast trends with high precision." },
    ];

    const addFeature = () => {
        const newFeatures = [...features, { title: "New Feature", description: "Descriptive text goes here." }];
        onContentChange?.({ ...content, features: newFeatures });
    };

    const removeFeature = (idx: number) => {
        const newFeatures = features.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, features: newFeatures });
    };

    const updateFeature = (idx: number, field: string, value: string) => {
        const newFeatures = [...features];
        newFeatures[idx] = { ...newFeatures[idx], [field]: value };
        onContentChange?.({ ...content, features: newFeatures });
    };

    return (
        <div className="mx-auto max-w-4xl" style={{ fontFamily }}>
            <h2 className="mb-12 text-center text-4xl font-extrabold text-gray-900 tracking-tight">
                <EditableText
                    value={content.title || "Neural Capabilities"}
                    onChange={(val) => onContentChange?.({ ...content, title: val })}
                    as="span"
                    placeholder="Section Title"
                />
            </h2>

            <div className="flex flex-col gap-6">
                {features.map((feature: any, idx: number) => (
                    <div
                        key={idx}
                        className="group relative flex items-center gap-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-blue-100"
                    >
                        {isSelected && (
                            <button
                                onClick={(e) => { e.stopPropagation(); removeFeature(idx); }}
                                className="absolute -top-2 -right-2 hidden group-hover:flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 border border-red-100 shadow-sm transition-colors"
                                title="Remove Feature"
                            >
                                <PiTrash className="h-4 w-4" />
                            </button>
                        )}

                        {/* Numbered Icon Box */}
                        <div className="flex-shrink-0 h-14 w-14 flex items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-500/20 font-bold text-xl">
                            {idx + 1}
                        </div>

                        {/* Text Content */}
                        <div className="flex-1 min-w-0">
                            <h3 className="mb-1 text-lg font-bold text-gray-900">
                                <EditableText
                                    value={feature.title}
                                    onChange={(val) => updateFeature(idx, "title", val)}
                                    as="span"
                                    placeholder="Feature Title"
                                />
                            </h3>
                            <p className="text-gray-500 font-medium">
                                <EditableText
                                    value={feature.description}
                                    onChange={(val) => updateFeature(idx, "description", val)}
                                    as="span"
                                    placeholder="Feature Description"
                                />
                            </p>
                        </div>
                    </div>
                ))}

                {isSelected && (
                    <button
                        onClick={addFeature}
                        className="w-full py-6 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all flex items-center justify-center font-medium"
                    >
                        <PiPlus className="h-5 w-5 mr-2" />
                        Add New Feature
                    </button>
                )}
            </div>
        </div>
    );
}
